/**
 * DynamicTable — Schema-driven table that auto-generates columns from Directus field metadata.
 *
 * When a field is added in the Directus Data Studio, it appears as a new column automatically.
 *
 * Features:
 * - Reads field metadata via useSchema()
 * - Builds TanStack Table columns dynamically
 * - Server-side pagination + sorting via Refine useTable
 * - Click-to-navigate to record detail
 * - Responsive: hides columns on smaller screens
 * - Column resize via drag handle
 */

import * as React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type ColumnResizeMode,
} from '@tanstack/react-table';
import { useList } from '@refinedev/core';
import type { DirectusField, DirectusFieldOption } from '@yesbos/api-client';
import { useSchema } from '@yesbos/hooks';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { formatLabel } from '../forms/field-renderer';
import { cn } from '../../lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  Search,
  Loader2,
  Plus,
} from 'lucide-react';

// ─── Column Builder ────────────────────────────────────────────────

/**
 * Build TanStack Table column definitions from Directus field metadata.
 */
function buildColumns(fields: DirectusField[]): ColumnDef<any>[] {
  return fields.map((field) => ({
    id: field.field,
    accessorKey: field.field,
    header: ({ column }: any) => (
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 data-[sorting=true]:font-bold"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        {formatLabel(field.field)}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ getValue }: any) => {
      const value = getValue();
      return formatCellValue(value, field);
    },
    enableSorting: true,
    enableResizing: true,
    size: 150,
    minSize: 80,
    maxSize: 500,
  }));
}

/**
 * Format a cell value based on field type/interface.
 */
function formatCellValue(value: unknown, field: DirectusField): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-[var(--color-muted-foreground)]">—</span>;
  }

  // Boolean
  if (field.type === 'boolean' || field.meta?.interface === 'boolean') {
    return (
      <Badge variant={value ? 'success' : 'secondary'}>
        {value ? 'Yes' : 'No'}
      </Badge>
    );
  }

  // Select dropdown — show human-readable label
  if (field.meta?.interface === 'select-dropdown') {
    const options = (field.meta?.options as any)?.choices as DirectusFieldOption[] ?? [];
    const selected = options.find((o) => String(o.value) === String(value));
    if (selected) {
      return <Badge variant="outline">{selected.text}</Badge>;
    }
  }

  // Date
  if (field.type === 'date') {
    try {
      return new Date(value as string).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return String(value);
    }
  }

  // DateTime
  if (field.type === 'dateTime' || field.type === 'timestamp') {
    try {
      return new Date(value as string).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(value);
    }
  }

  // Truncate long text
  const str = String(value);
  if (str.length > 50) {
    return str.substring(0, 50) + '...';
  }

  return str;
}

// ─── Props ─────────────────────────────────────────────────────────

export interface DynamicTableProps {
  /** Directus collection name */
  collection: string;
  /** Title above the table */
  title?: string;
  /** Click handler for row (usually navigate to detail) */
  onRowClick?: (record: any) => void;
  /** Handler for "Add" button */
  onAddClick?: () => void;
  /** Fields to exclude from the table */
  excludeFields?: string[];
  /** Maximum columns to show (responsive) */
  maxColumns?: number;
  /** Initial page size */
  pageSize?: number;
  /** Additional Directus filters */
  permanentFilter?: Record<string, unknown>;
  /** Enable column resize handles */
  enableColumnResize?: boolean;
  /** Custom class name */
  className?: string;
  /** Row-level action buttons rendered in a trailing Actions column */
  rowActions?: (record: any) => React.ReactNode;
}

// ─── Component ─────────────────────────────────────────────────────

export function DynamicTable({
  collection,
  title,
  onRowClick,
  onAddClick,
  excludeFields = [],
  maxColumns = 8,
  pageSize: initialPageSize = 25,
  enableColumnResize = true,
  permanentFilter,
  className,
  rowActions,
}: DynamicTableProps) {
  const { tableFields, isLoading: schemaLoading } = useSchema(collection);

  // Limit visible columns
  const visibleFields = React.useMemo(() => {
    const filtered = tableFields.filter((f) => !excludeFields.includes(f.field));
    return filtered.slice(0, maxColumns);
  }, [tableFields, excludeFields, maxColumns]);

  // Search state
  const [searchTerm, setSearchTerm] = React.useState('');

  // Pagination state
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });

  // Sorting state
  const [sorting, setSorting] = React.useState<SortingState>([]);

  // Column resize mode
  const columnResizeMode: ColumnResizeMode = 'onChange';

  // Build Refine sorters from TanStack sorting
  const refineSorters = React.useMemo(
    () =>
      sorting.map((s) => ({
        field: s.id,
        order: s.desc ? ('desc' as const) : ('asc' as const),
      })),
    [sorting],
  );

  // Build Refine filters from search term
  const refineFilters = React.useMemo(() => {
    const filters: any[] = [];

    // Add search filter (search first text field)
    if (searchTerm && visibleFields.length > 0) {
      const textField = visibleFields.find(
        (f) => f.type === 'string' || f.type === 'text',
      );
      if (textField) {
        filters.push({
          field: textField.field,
          operator: 'contains',
          value: searchTerm,
        });
      }
    }

    return filters;
  }, [searchTerm, visibleFields]);

  // Fetch data via Refine useList
  const { data, isLoading: dataLoading } = useList({
    resource: collection,
    pagination: {
      current: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    },
    sorters: refineSorters,
    filters: refineFilters,
    meta: permanentFilter ? { filter: permanentFilter } : undefined,
  });

  const records = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.ceil(total / pagination.pageSize);

  // Build columns (data columns + optional actions column)
  const columns = React.useMemo(() => {
    const dataCols = buildColumns(visibleFields);
    if (!rowActions) return dataCols;
    const actionsCol: ColumnDef<any> = {
      id: '__actions__',
      header: () => <span className="text-xs font-medium text-[var(--color-muted-foreground)]">Actions</span>,
      cell: ({ row }: any) => (
        <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
          {rowActions(row.original)}
        </div>
      ),
      enableSorting: false,
      enableResizing: false,
      size: 120,
    };
    return [...dataCols, actionsCol];
  }, [visibleFields, rowActions]);

  // TanStack Table instance
  const table = useReactTable({
    data: records,
    columns,
    pageCount,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    ...(enableColumnResize && {
      columnResizeMode,
      enableColumnResizing: true,
    }),
  });

  const isLoading = schemaLoading || dataLoading;
  const tableTitle = title ?? formatLabel(collection);

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-xl">{tableTitle}</CardTitle>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[var(--color-muted-foreground)]" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="pl-8 w-[250px]"
            />
          </div>
          {/* Add button */}
          {onAddClick && (
            <Button onClick={onAddClick} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add {formatLabel(collection).replace(/s$/, '')}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
            <span className="ml-3 text-sm text-[var(--color-muted-foreground)]">
              Loading...
            </span>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="rounded-md border border-[var(--color-border)]">
              <Table style={enableColumnResize ? { width: table.getCenterTotalSize() } : undefined}>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          style={enableColumnResize ? { width: header.getSize() } : undefined}
                          className="relative"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                          {/* Resize handle */}
                          {enableColumnResize && header.column.getCanResize() && (
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className={cn(
                                'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
                                'hover:bg-[var(--color-primary)]/50',
                                header.column.getIsResizing() && 'bg-[var(--color-primary)]',
                              )}
                            />
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className={cn(
                          onRowClick && 'cursor-pointer hover:bg-[var(--color-muted)]',
                        )}
                        onClick={() => onRowClick?.(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            style={enableColumnResize ? { width: cell.column.getSize() } : undefined}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center text-[var(--color-muted-foreground)]"
                      >
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between py-4">
              <p className="text-sm text-[var(--color-muted-foreground)]">
                {total} record{total !== 1 ? 's' : ''} total
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-[var(--color-muted-foreground)]">
                  Page {pagination.pageIndex + 1} of {pageCount || 1}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => table.setPageIndex(pageCount - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
